import { axiosPrivate } from 'api/client/classes/AxiosInstance';
import PropTypes from 'prop-types';
import React, { createContext, useEffect, useState } from 'react';
import { createLogger } from 'utils/logger';

// Create a logger for the account provider
const logger = createLogger('AccountProvider');

/**
 * Represents the account settings object
 *
 * @typedef {Object} AccountSettings
 * @property {string} id - The account ID
 * @property {Object} profile - The profile information
 * @property {string} profile.bio - The biography of the user
 * @property {Object} profile.media - The media information
 * @property {string} profile.media.avatar - The URL of the user's avatar
 * @property {string} profile.media.banner - The URL of the user's banner image
 * @property {Object} profile.name - The name information
 * @property {string} profile.name.first - The user's first name
 * @property {string} profile.name.last - The user's last name
 * @property {string} profile.nameDisplay - The display name of the user
 * @property {string} profile.namePronunciation - The pronunciation of the user's name
 * @property {string} profile.pronouns - The pronouns of the user
 * @property {Object} profile.social - The social media information
 * @property {string} profile.social.facebook - The user's Facebook profile URL
 * @property {string} profile.social.github - The user's GitHub profile URL
 * @property {string} profile.social.instagram - The user's Instagram profile URL
 * @property {string} profile.social.linkedin - The user's LinkedIn profile URL
 * @property {string} profile.social.twitter - The user's Twitter profile URL
 * @property {string} profile.social.youtube - The user's YouTube profile URL
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.1
 */
export const accountSettings = {
  id: '',
  profile: {
    bio: '',
    media: {
      avatar: '',
      banner: ''
    },
    name: {
      first: '',
      last: ''
    },
    nameDisplay: '',
    namePronunciation: '',
    pronouns: '',
    social: {
      facebook: '',
      github: '',
      instagram: '',
      linkedin: '',
      twitter: '',
      youtube: ''
    }
  }
};

/**
 * Create AccountContext
 * @type {React.Context<{}>}
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.1
 */
export const AccountContext = createContext(accountSettings);

/**
 * Provides the account context for the application
 *
 * @param {Object} props - The component props
 * @returns {JSX.Element} The account provider component
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.2
 */
export const AccountProvider = props => {
  const [account, setAccount] = useState(accountSettings);

  // Safely deep-merge account structures without dropping nested fields
  const mergeAccount = (prev = {}, incoming = {}) => {
    const safeObj = v =>
      v && typeof v === 'object' && !Array.isArray(v) ? v : {};

    const prevProfile = safeObj(prev.profile);
    const inProfile = safeObj(incoming.profile);

    const prevName = safeObj(prevProfile.name);
    const inName = safeObj(inProfile.name);

    const prevMedia = safeObj(prevProfile.media);
    const inMedia = safeObj(inProfile.media);

    const prevSocial = safeObj(prevProfile.social);
    const inSocial = safeObj(inProfile.social);

    return {
      ...prev,
      ...incoming,
      profile: {
        ...prevProfile,
        ...inProfile,
        // Deep merge key sub-objects so partial payloads don't wipe existing fields
        name: { ...prevName, ...inName },
        media: { ...prevMedia, ...inMedia },
        social: { ...prevSocial, ...inSocial }
      }
    };
  };

  // Register token refresh listener to update account data
  useEffect(() => {
    logger.debug('Setting up token refresh listener for account data');

    // Register with axiosPrivate to be notified when tokens are refreshed
    const unsubscribe = axiosPrivate.onTokenRefresh(authData => {
      // Check if account data is included in the refresh response
      if (
        authData &&
        authData.account &&
        Object.keys(authData.account).length > 0
      ) {
        logger.debug('Received account data in token refresh response');

        // Update account state with the new data
        setAccount(prev => {
          const updated = mergeAccount(prev, authData.account);
          logger.debug('Account data updated from token refresh');
          return updated;
        });
      } else {
        logger.debug('No account data in token refresh response');
      }
    });

    logger.debug('Account provider token refresh listener active');

    // Clean up the listener when the component unmounts
    return () => {
      logger.debug('Removing account provider token refresh listener');
      unsubscribe();
    };
  }, []);

  const values = {
    account,
    setAccount
  };

  return (
    <AccountContext.Provider value={values}>
      {props.children}
    </AccountContext.Provider>
  );
};

AccountProvider.propTypes = {
  children: PropTypes.node
};

export default AccountProvider;
